import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { API } from "@services/http.service";
import showErrMessage from "@components/ShowErrMessage";
import showMessage from "@components/ShowMessage";

import "./styles.scss";

/**
 * "Satış temsilcinizin referans kodu" ilk-giriş popup'ı (İşletmeni Getir tamamlayıcısı).
 *
 * Amaç: kayıt sırasında satış temsilcisi kodunu girmeyi unutmuş ya da bu özellik yokken
 * kaydolmuş işletmelerin kodu SONRADAN girebilmesi. Kod girilirse kayıt, temsilcinin
 * "Nokta Kayıt" hedefine sayılır. Puan kazanımı YOKTUR.
 *
 * Akış:
 *  - Login sonrası modalların EN SONUNCUSU'dur (izin onayı → hoşgeldin popup → bu popup).
 *  - SADECE İLK GİRİŞTE çıkar. Bunun için AYRI BİR KOLON YOKTUR: hoşgeldin popup'ı ile aynı
 *    bayrak (auth_user.welcome_popup_seen) paylaşılır. Durumu App.js'teki PostLoginModals
 *    sorar ve firstLogin ile verir; "görüldü" işaretini de o atar.
 *  - Üyeye sorulabilir mi kararı SUNUCUDA verilir: GET /employee/salesRepCodePrompt/status
 *    (onaylı USER2 && kayıtta hiç referans kodu yok).
 *  - OPSİYONELDİR: kullanıcı boş bırakıp kapatabilir.
 *
 * Sözleşme (WelcomePopup / CommercialConsentModal ile aynı):
 *  - enabled: true olana kadar bekler (önceki modal çözülene dek).
 *  - firstLogin: null = henüz bilinmiyor, true/false = kullanıcının ilk girişi mi?
 *  - onShown: popup gerçekten gösterildiğinde çağrılır (ilk-giriş bayrağını kapatmak için).
 *  - onResolved: akış bittiğinde (gösterilmeyecekse ya da kapatıldığında) bir kez çağrılır.
 */
const SalesRepCodePopup = ({ enabled = true, firstLogin = null, onShown, onResolved }) => {
  const isAuthenticated = useSelector((store) => store.auth.isAuthenticated);
  // Sorumluluk Reddi (disclaimer) akışından SONRA çıkmalı (diğer modallarla aynı önkoşul).
  const disclaimerInitialized = useSelector(
    (store) => store.disclaimer.initialized
  );
  const disclaimerRequired = useSelector((store) => store.disclaimer.required);

  const [show, setShow] = useState(false);
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // "idle" | "loading" | "valid" | "invalid"
  const [codeStatus, setCodeStatus] = useState("idle");
  const [codeMessage, setCodeMessage] = useState("");

  // Aynı oturumda durum sorgusunun tekrar tekrar çalışmasını engeller.
  const checkedRef = useRef(false);
  // onResolved yalnızca bir kez tetiklensin.
  const resolvedRef = useRef(false);
  const resolve = () => {
    if (resolvedRef.current) return;
    resolvedRef.current = true;
    if (onResolved) onResolved();
  };

  useEffect(() => {
    if (!isAuthenticated || !enabled || checkedRef.current) return;
    // Disclaimer henüz çözülmediyse ya da hâlâ zorunluysa bekle.
    if (!disclaimerInitialized || disclaimerRequired !== false) return;
    // İlk-giriş durumu henüz gelmediyse bekle (orchestrator sorar).
    if (firstLogin === null) return;
    checkedRef.current = true;

    // İlk giriş değilse hiç sorma — popup yalnızca ilk girişte çıkar.
    if (firstLogin !== true) {
      resolve();
      return;
    }

    let active = true;
    (async () => {
      try {
        const response = await API.get("/employee/salesRepCodePrompt/status");
        if (!active) return;
        if (response?.data === true) {
          setShow(true);
          // Gösterildi → ilk-giriş bayrağı kapatılsın ki bir daha açılmasın
          // (kullanıcı kapatmadan çıksa bile tekrar gösterilmez).
          if (onShown) onShown();
        } else {
          resolve();
        }
      } catch (error) {
        // Durum alınamazsa popup gösterme; sessizce devret.
        resolve();
      }
    })();

    return () => {
      active = false;
    };
  }, [isAuthenticated, enabled, disclaimerInitialized, disclaimerRequired, firstLogin]);

  // Kod girildikçe canlı doğrulama (kayıt formundaki ile aynı kural:
  // kod bir işletmeye ait OLMAMALI → excludedRole=USER2).
  const validateCode = async (value) => {
    const trimmed = (value || "").trim();
    if (!trimmed) {
      setCodeStatus("idle");
      setCodeMessage("");
      return;
    }
    setCodeStatus("loading");
    try {
      const res = await API.get(
        `/employee/public/validate-reference-code/${trimmed}?excludedRole=USER2`
      );
      setCodeStatus("valid");
      setCodeMessage(`${res.data} adlı satış temsilcisine ait kod.`);
    } catch (error) {
      setCodeStatus("invalid");
      setCodeMessage(
        "Geçersiz referans kodu. Lütfen satış temsilcinizin kodunu kontrol ediniz."
      );
    }
  };

  const handleClose = () => {
    setShow(false);
    resolve();
  };

  const handleSubmit = async () => {
    const trimmed = code.trim();
    if (!trimmed || submitting) return;
    setSubmitting(true);
    try {
      const res = await API.post(
        `/employee/salesRepCodePrompt/save?code=${encodeURIComponent(trimmed)}`
      );
      showMessage(
        res?.data && res.data !== "1"
          ? `${res.data} adlı satış temsilciniz ile eşleştirildiniz.`
          : "Satış temsilcisi kodunuz kaydedildi."
      );
      handleClose();
    } catch (error) {
      showErrMessage(
        error?.message ||
          "Referans kodu kaydedilemedi. Lütfen kodu kontrol edip tekrar deneyin."
      );
      setSubmitting(false);
    }
  };

  if (!show) return null;

  return (
    <div
      className="sales-rep-code-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="salesRepCodeTitle"
    >
      {/* Modal yalnızca "Kodum Yok" ya da (X) ile kapanır; dış alana tıklamak kapatmaz —
          kullanıcı yanlışlıkla kapatıp kodu girme şansını kaybetmesin (popup bir kez çıkar). */}
      <div className="sales-rep-code-modal">
        <button
          type="button"
          className="sales-rep-code-close"
          aria-label="Kapat"
          onClick={handleClose}
        >
          <i className="fas fa-times"></i>
        </button>

        <div className="sales-rep-code-header">
          <h4 id="salesRepCodeTitle">Satış Temsilcinizin Kodu</h4>
        </div>

        <div className="sales-rep-code-body">
          <p>
            Satış temsilcinizin 6 haneli referans kodunu biliyorsanız aşağıya
            girebilirsiniz.
          </p>

          <input
            type="text"
            className={`form-control ${
              codeStatus === "invalid" ? "is-invalid" : ""
            } ${codeStatus === "valid" ? "is-valid" : ""}`}
            placeholder="Örn: A1B2C3"
            maxLength={6}
            value={code}
            onChange={(e) => {
              setCode(e.target.value.toUpperCase());
              setCodeStatus("idle");
              setCodeMessage("");
            }}
            onBlur={(e) => validateCode(e.target.value)}
            disabled={submitting}
          />

          <span
            className={`sales-rep-code-feedback ${
              codeStatus === "valid" ? "is-valid-text" : ""
            } ${codeStatus === "invalid" ? "is-invalid-text" : ""}`}
          >
            {codeStatus === "loading" ? "Kod kontrol ediliyor..." : codeMessage}
          </span>
        </div>

        <div className="sales-rep-code-footer">
          <button
            type="button"
            className="btn btn-outline-secondary px-4"
            onClick={handleClose}
            disabled={submitting}
          >
            Kodum Yok
          </button>
          <button
            type="button"
            className="btn btn-blue px-4"
            onClick={handleSubmit}
            disabled={submitting || !code.trim() || codeStatus === "invalid"}
          >
            {submitting && (
              <span className="spinner-border spinner-border-sm me-2" />
            )}
            Kaydet
          </button>
        </div>
      </div>
    </div>
  );
};

export default SalesRepCodePopup;
